import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { imgUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { ArrowLeft, MapPin, Eye, Mail, Phone, MessageSquare, ShieldCheck, Truck, AlertTriangle, Heart, Send, Clock, Gavel, Check, X, Trash2, Share2, Copy } from "lucide-react";
import { toast } from "sonner";
import SellerReviews from "@/components/SellerReviews";
import VerifiedBadge from "@/components/VerifiedBadge";
import { RatingBadge } from "@/components/Stars";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const TEMPLATES = ["Is dit product nog beschikbaar?", "Biedt u discrete verzending aan?", "Wat is de laagste prijs?"];

export default function ProductDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [bidOpen, setBidOpen] = useState(false);
  const [bidAmount, setBidAmount] = useState("");
  const [bidData, setBidData] = useState(null);
  const [placingBid, setPlacingBid] = useState(false);
  const [counterFor, setCounterFor] = useState(null);
  const [counterAmt, setCounterAmt] = useState("");
  const [copied, setCopied] = useState(false);
  const onStats = useCallback((r) => setProduct((prev) => (prev ? { ...prev, seller_rating: r } : prev)), []);

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => {
      if (data.listing_type === "service") { navigate(`/dienst/${id}`, { replace: true, state: { counted: true } }); return; }
      setProduct(data);
    }).catch(() => navigate("/"));
  }, [id, navigate]);

  const loadBids = () => {
    if (user) api.get(`/products/${id}/bids`).then(({ data }) => setBidData(data)).catch(() => {});
  };

  useEffect(() => { loadBids(); }, [id, user]);

  if (!product) return <div className="mx-auto max-w-7xl px-6 py-20 text-center text-zinc-500">Laden...</div>;

  const isOwner = user && user.id === product.seller_id;
  const images = product.images || [];
  const minBid = (Number(product.price) * 0.8);
  const myBid = bidData && !bidData.is_seller ? (bidData.bids || [])[0] : null;

  const shareUrl = `${window.location.origin}/api/share/product/${product.id}`;
  const shareText = `${product.title} — € ${Number(product.price).toFixed(2)} op Fetshop.nl`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link gekopieerd — plak 'm in je advertentie of post");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Kopiëren mislukt");
    }
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: product.title, text: shareText, url: shareUrl }); } catch {}
    } else {
      copyLink();
    }
  };

  const placeBid = async () => {
    if (!user) { navigate("/auth"); return; }
    const amt = parseFloat(bidAmount);
    if (!amt || amt < minBid) { toast.error(`Minimaal € ${minBid.toFixed(2)} (80% van de vraagprijs)`); return; }
    setPlacingBid(true);
    try {
      await api.post("/bids", { product_id: product.id, amount: amt });
      toast.success("Je bod is verstuurd naar de verkoper");
      setBidOpen(false);
      setBidAmount("");
      loadBids();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kon bod niet plaatsen");
    } finally {
      setPlacingBid(false);
    }
  };

  const setBidStatus = async (bidId, status) => {
    try {
      await api.patch(`/bids/${bidId}/status?status=${status}`);
      toast.success(status === "accepted" ? "Bod geaccepteerd" : "Bod afgewezen");
      loadBids();
      api.get(`/products/${id}`).then(({ data }) => setProduct(data));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Actie mislukt");
    }
  };

  const sendCounter = async () => {
    const amt = parseFloat(counterAmt);
    if (!amt || amt <= 0) { toast.error("Vul een geldig bedrag in"); return; }
    try {
      await api.patch(`/bids/${counterFor}/counter?amount=${amt}`);
      toast.success("Tegenbod verstuurd naar de koper");
      setCounterFor(null); setCounterAmt("");
      loadBids();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kon tegenbod niet versturen");
    }
  };

  const respondCounter = async (bidId, action) => {
    try {
      await api.patch(`/bids/${bidId}/respond?action=${action}`);
      toast.success(action === "accept" ? "Tegenbod geaccepteerd" : "Tegenbod afgewezen");
      loadBids();
      api.get(`/products/${id}`).then(({ data }) => setProduct(data));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Actie mislukt");
    }
  };

  const deleteBid = async (bidId, asSeller) => {
    if (!window.confirm(asSeller ? "Dit bod verwijderen?" : "Weet je zeker dat je je bod wilt intrekken?")) return;
    try {
      await api.delete(`/bids/${bidId}`);
      toast.success(asSeller ? "Bod verwijderd" : "Je bod is ingetrokken");
      loadBids();
      api.get(`/products/${id}`).then(({ data }) => setProduct(data));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Actie mislukt");
    }
  };

  const contactBidder = async (bidId) => {
    try {
      const { data } = await api.post(`/bids/${bidId}/contact`);
      navigate(`/berichten?c=${data.conversation_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kon geen contact openen");
    }
  };

  const sendMessage = async () => {
    if (!user) { navigate("/auth"); return; }
    if (!msg.trim()) return;
    setSending(true);
    try {
      await api.post("/messages", { product_id: product.id, text: msg });
      toast.success("Bericht verzonden naar verkoper");
      setChatOpen(false);
      setMsg("");
      navigate("/berichten");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kon bericht niet versturen");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <button data-testid="back-button" onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-100 mb-6">
        <ArrowLeft className="h-4 w-4" /> {t("pd.back")}
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Gallery */}
        <div>
          <div className="relative aspect-square overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950">
            {images.length ? (
              <img src={imgUrl(images[active])} alt={product.title} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full items-center justify-center text-zinc-700 font-serif">Geen foto beschikbaar</div>
            )}
            <span className="absolute top-4 left-4 rounded-full bg-rose-950/70 backdrop-blur text-rose-300 border border-rose-800/50 text-[11px] font-mono uppercase tracking-wider px-3 py-1">
              {product.condition}
            </span>
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-3 overflow-x-auto">
              {images.map((im, i) => (
                <button key={i} onClick={() => setActive(i)} className={`h-20 w-20 shrink-0 overflow-hidden rounded-lg border-2 ${i === active ? "border-rose-500" : "border-zinc-800"}`}>
                  <img src={imgUrl(im)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-rose-400 mb-2">{product.category}</div>
          <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-zinc-100 leading-tight">{product.title}</h1>
          <div className="mt-4 flex items-center gap-4 text-sm text-zinc-500">
            <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {product.location || "Onbekend"}</span>
            <span className="flex items-center gap-1"><Eye className="h-4 w-4" /> {product.views} {t("pd.views")}</span>
            {product.bid_count > 0 && (
              <span className="flex items-center gap-1 text-rose-300"><Gavel className="h-4 w-4" /> {product.bid_count} {product.bid_count === 1 ? "bod" : "biedingen"}</span>
            )}
          </div>
          <div className="mt-6 font-mono text-4xl font-semibold text-rose-400">€ {Number(product.price).toFixed(2)}</div>
          {product.highest_bid > 0 && (
            <div data-testid="highest-bid-banner" className="mt-3 inline-flex items-center gap-2 rounded-lg bg-emerald-950/30 border border-emerald-800/40 px-3 py-2 text-sm">
              <Gavel className="h-4 w-4 text-emerald-400" />
              <span className="text-emerald-300/80">Hoogste bod op dit moment:</span>
              <span className="font-mono font-semibold text-emerald-300">€ {Number(product.highest_bid).toFixed(2)}</span>
            </div>
          )}

          <div data-testid="share-bar" className="mt-6 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
            <div className="flex items-center gap-2 text-sm font-medium text-zinc-300 mb-3">
              <Share2 className="h-4 w-4 text-rose-400" /> Deel deze advertentie
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="share-native"
                onClick={nativeShare}
                className="flex items-center gap-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-xs font-medium px-3 py-2 transition-colors"
              >
                <Share2 className="h-3.5 w-3.5" /> Delen
              </button>
              <a
                data-testid="share-whatsapp"
                href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900/60 text-emerald-300 hover:bg-zinc-800 text-xs font-medium px-3 py-2 transition-colors"
              >WhatsApp</a>
              <a
                data-testid="share-facebook"
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900/60 text-sky-300 hover:bg-zinc-800 text-xs font-medium px-3 py-2 transition-colors"
              >Facebook</a>
              <a
                data-testid="share-twitter"
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900/60 text-zinc-200 hover:bg-zinc-800 text-xs font-medium px-3 py-2 transition-colors"
              >X / Twitter</a>
              <a
                data-testid="share-telegram"
                href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900/60 text-sky-200 hover:bg-zinc-800 text-xs font-medium px-3 py-2 transition-colors"
              >Telegram</a>
              <button
                data-testid="share-copy"
                onClick={copyLink}
                className="flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900/60 text-zinc-300 hover:bg-zinc-800 text-xs font-medium px-3 py-2 transition-colors"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "Gekopieerd" : "Kopieer link"}
              </button>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
            <h2 className="font-serif text-xl text-zinc-200 mb-2">{t("pd.description")}</h2>
            <p className="text-sm text-zinc-400 leading-relaxed whitespace-pre-line">{product.description}</p>
            {(product.pickup || product.shipping) && (
              <div data-testid="shipping-info" className="mt-4 flex flex-wrap gap-2">
                {product.pickup && (
                  <span className="flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-1.5 text-xs text-zinc-300">
                    <MapPin className="h-3.5 w-3.5 text-rose-400" /> Ophalen mogelijk
                  </span>
                )}
                {product.shipping && (
                  <span className="flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-1.5 text-xs text-zinc-300">
                    <Truck className="h-3.5 w-3.5 text-rose-400" /> {Number(product.shipping_cost) > 0 ? `Verzenden — € ${Number(product.shipping_cost).toFixed(2)}` : "Gratis verzenden"}
                  </span>
                )}
              </div>
            )}
            {product.delivery && (
              <div className="mt-4 flex items-center gap-2 text-sm text-zinc-400">
                <Truck className="h-4 w-4 text-rose-400" /> {product.delivery}
              </div>
            )}
          </div>

          {/* Seller box */}
          <div className="mt-6 rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-rose-950/50 border border-rose-800/40 text-rose-300 font-medium">
                {(product.seller_name || "V").charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="text-sm font-medium text-zinc-200 flex items-center gap-1.5">
                  {product.seller_name || "Verkoper"} <VerifiedBadge show={product.seller_verified} testid="seller-verified-badge" />
                </div>
                <div className="text-xs text-zinc-500 flex items-center gap-1"><Clock className="h-3 w-3" /> {t("pd.responds")}</div>
                <RatingBadge rating={product.seller_rating} testid="seller-rating-badge" />
              </div>
            </div>

            {!isOwner && (
              <div className="mt-4 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    data-testid="open-chat-button"
                    onClick={() => (user ? setChatOpen(true) : navigate("/auth"))}
                    className="flex items-center justify-center gap-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-medium px-4 py-3 shadow-lg shadow-rose-950/50 transition-all hover:scale-[1.01]"
                  >
                    <MessageSquare className="h-4 w-4" /> {t("pd.chat_seller")}
                  </button>
                  <button
                    data-testid="open-bid-button"
                    disabled={product.status !== "active"}
                    onClick={() => (user ? (setBidAmount(Math.max(minBid, Number(product.highest_bid || 0)).toFixed(2)), setBidOpen(true)) : navigate("/auth"))}
                    className="flex items-center justify-center gap-2 rounded-lg border border-rose-700/60 bg-rose-950/30 text-rose-200 hover:bg-rose-950/60 font-medium px-4 py-3 transition-all disabled:opacity-50"
                  >
                    <Gavel className="h-4 w-4" /> Doe een bod
                  </button>
                </div>
                {myBid && (
                  <div data-testid="my-bid-status" className="rounded-lg border border-zinc-800 bg-zinc-900/60 px-4 py-2.5 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-zinc-400">Jouw bod: <span className="font-mono text-rose-300">€ {Number(myBid.amount).toFixed(2)}</span></span>
                      <span className={`text-xs uppercase font-medium ${myBid.status === "accepted" ? "text-emerald-400" : myBid.status === "declined" ? "text-rose-400" : myBid.status === "countered" ? "text-amber-400" : "text-amber-400"}`}>
                        {myBid.status === "accepted" ? "Geaccepteerd" : myBid.status === "declined" ? "Afgewezen" : myBid.status === "countered" ? "Tegenbod" : "In afwachting"}
                      </span>
                    </div>
                    {myBid.status === "countered" && (
                      <div className="mt-3 rounded-md bg-amber-950/20 border border-amber-800/40 p-3">
                        <div className="text-xs text-amber-200 mb-2">De verkoper stelt <span className="font-mono font-semibold">€ {Number(myBid.counter_amount).toFixed(2)}</span> voor.</div>
                        <div className="flex gap-2">
                          <button data-testid="accept-counter-button" onClick={() => respondCounter(myBid.id, "accept")} className="flex-1 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium px-3 py-2">Accepteer € {Number(myBid.counter_amount).toFixed(2)}</button>
                          <button data-testid="decline-counter-button" onClick={() => respondCounter(myBid.id, "decline")} className="rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs px-3 py-2">Afwijzen</button>
                        </div>
                      </div>
                    )}
                    {(myBid.status === "pending" || myBid.status === "countered") && (
                      <button
                        data-testid="withdraw-bid-button"
                        onClick={() => deleteBid(myBid.id, false)}
                        className="mt-3 w-full flex items-center justify-center gap-1.5 rounded-md border border-zinc-700 text-zinc-300 hover:text-rose-300 hover:border-rose-900/60 text-xs font-medium px-3 py-2 transition-colors"
                      >
                        <X className="h-3.5 w-3.5" /> Bod intrekken
                      </button>
                    )}
                  </div>
                )}
                <div className="flex items-start gap-2 rounded-lg border border-zinc-800 bg-zinc-900/60 px-4 py-3 text-xs text-zinc-400">
                  <ShieldCheck className="h-4 w-4 text-rose-400 shrink-0 mt-0.5" />
                  <span>{t("pd.contact_note")}</span>
                </div>
              </div>
            )}
            {isOwner && (
              <div className="mt-4 space-y-3">
                <div className="rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3 text-sm text-zinc-500">
                  {t("pd.own")} <Link to="/dashboard" className="text-rose-400">{t("pd.dashboard")}</Link>.
                </div>
                {bidData && bidData.is_seller && (
                  <div data-testid="seller-bids" className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-zinc-200 mb-3">
                      <Gavel className="h-4 w-4 text-rose-400" /> Biedingen ({bidData.bids.length})
                    </div>
                    {bidData.bids.length === 0 ? (
                      <p className="text-xs text-zinc-500">Nog geen biedingen. Minimaal bod: € {bidData.min_bid.toFixed(2)}</p>
                    ) : (
                      <div className="space-y-2">
                        {bidData.bids.map((b) => (
                          <div key={b.id} data-testid={`bid-${b.id}`} className="flex items-center justify-between gap-2 rounded-md bg-zinc-950 border border-zinc-800 px-3 py-2">
                            <div className="min-w-0">
                              <div className="text-sm text-zinc-200 truncate">{b.buyer_name || "Koper"} — <span className="font-mono text-rose-300">€ {Number(b.amount).toFixed(2)}</span></div>
                              <div className="text-[10px] uppercase text-zinc-500">
                                {b.status === "accepted" ? "Geaccepteerd" : b.status === "declined" ? "Afgewezen" : b.status === "countered" ? `Tegenbod € ${Number(b.counter_amount).toFixed(2)} verstuurd` : "In afwachting"}
                              </div>
                            </div>
                            <div className="flex gap-1 shrink-0">
                              <button data-testid={`contact-bidder-${b.id}`} onClick={() => contactBidder(b.id)} title="Neem contact op met bieder" className="p-2 rounded-md text-sky-400 hover:bg-zinc-800"><MessageSquare className="h-4 w-4" /></button>
                              {(b.status === "pending" || b.status === "countered") && (
                                <>
                                  <button data-testid={`accept-bid-${b.id}`} onClick={() => setBidStatus(b.id, "accepted")} title="Accepteren" className="p-2 rounded-md text-emerald-400 hover:bg-zinc-800"><Check className="h-4 w-4" /></button>
                                  <button data-testid={`counter-bid-${b.id}`} onClick={() => { setCounterFor(b.id); setCounterAmt(String(Math.round(Number(product.price) * 0.9))); }} title="Tegenbod" className="p-2 rounded-md text-amber-400 hover:bg-zinc-800"><Gavel className="h-4 w-4" /></button>
                                  <button data-testid={`decline-bid-${b.id}`} onClick={() => setBidStatus(b.id, "declined")} title="Afwijzen" className="p-2 rounded-md text-rose-400 hover:bg-zinc-800"><X className="h-4 w-4" /></button>
                                </>
                              )}
                              {b.status !== "accepted" && (
                                <button data-testid={`delete-bid-${b.id}`} onClick={() => deleteBid(b.id, true)} title="Bod verwijderen" className="p-2 rounded-md text-zinc-500 hover:text-rose-400 hover:bg-zinc-800"><Trash2 className="h-4 w-4" /></button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center gap-2 text-xs text-amber-500/80">
            <AlertTriangle className="h-4 w-4" /> {t("pd.safety")}
          </div>
        </div>
      </div>

      <SellerReviews onStats={onStats} sellerId={product.seller_id} sellerName={product.seller_name} productId={product.id} />

      <Dialog open={chatOpen} onOpenChange={setChatOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Bericht aan {product.seller_name}</DialogTitle>
            <DialogDescription className="text-zinc-500">Stel je vraag over dit product. Deel geen betaalgegevens.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2 mb-2">
            {TEMPLATES.map((t) => (
              <button key={t} onClick={() => setMsg(t)} className="rounded-full border border-zinc-800 bg-zinc-900 px-3 py-1 text-xs text-zinc-400 hover:text-rose-300">
                {t}
              </button>
            ))}
          </div>
          <textarea
            data-testid="chat-message-input"
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            rows={4}
            placeholder="Typ je bericht..."
            className="w-full rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-rose-700 focus:outline-none"
          />
          <button
            data-testid="chat-send-message-button"
            onClick={sendMessage}
            disabled={sending}
            className="flex items-center justify-center gap-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-medium px-5 py-3 transition-all disabled:opacity-60"
          >
            <Send className="h-4 w-4" /> {sending ? "Versturen..." : "Verstuur bericht"}
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={bidOpen} onOpenChange={setBidOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Doe een bod</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Vraagprijs: € {Number(product.price).toFixed(2)}. Minimaal bod: <span className="text-rose-300 font-medium">€ {minBid.toFixed(2)}</span> (80% van de vraagprijs).
              {product.highest_bid > 0 && (
                <span className="block mt-1 text-emerald-300">Hoogste bod nu: € {Number(product.highest_bid).toFixed(2)} — bied hoger om kans te maken.</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">€</span>
            <input
              data-testid="bid-amount-input"
              type="number"
              step="0.01"
              min={minBid}
              value={bidAmount}
              onChange={(e) => setBidAmount(e.target.value)}
              className="w-full rounded-lg border border-zinc-800 bg-zinc-900 pl-8 pr-4 py-3 text-lg font-mono text-zinc-100 focus:border-rose-700 focus:outline-none"
            />
          </div>
          {bidAmount && parseFloat(bidAmount) < minBid && (
            <p className="text-xs text-rose-400">Je bod is lager dan het minimum van € {minBid.toFixed(2)}.</p>
          )}
          <button
            data-testid="place-bid-button"
            onClick={placeBid}
            disabled={placingBid}
            className="flex items-center justify-center gap-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-medium px-5 py-3 transition-all disabled:opacity-60"
          >
            <Gavel className="h-4 w-4" /> {placingBid ? "Versturen..." : "Verstuur bod"}
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!counterFor} onOpenChange={(o) => !o && setCounterFor(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Tegenbod doen</DialogTitle>
            <DialogDescription className="text-zinc-500">Stel de koper een ander bedrag voor. De koper krijgt een melding en kan accepteren of afwijzen.</DialogDescription>
          </DialogHeader>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">€</span>
            <input
              data-testid="counter-amount-input"
              type="number" step="0.01" min="0"
              value={counterAmt}
              onChange={(e) => setCounterAmt(e.target.value)}
              className="w-full rounded-lg border border-zinc-800 bg-zinc-900 pl-8 pr-4 py-3 text-lg font-mono text-zinc-100 focus:border-rose-700 focus:outline-none"
            />
          </div>
          <button
            data-testid="send-counter-button"
            onClick={sendCounter}
            className="flex items-center justify-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-amber-950 font-medium px-5 py-3 transition-all"
          >
            <Gavel className="h-4 w-4" /> Verstuur tegenbod
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
